<template>
  <div class="w-full bg-white">
    <div
      class="w-full h-20 bg-panels-gradient"
      v-is-authorized="{ roles: [role], permissions: ['can_view_members'] }"
    >
      <ul class="h-full flex justify-center md:justify-start overflow-x-auto">
        <li class="mx-3 md:mx-6 flex flex-row flex-none">
          <a
            href="#"
            class="h-full border-b-4 border-transparent hover:border-brand no-underline whitespace-no-wrap text-grey-darkest uppercase font-semibold text-xs md:text-base flex items-center"
            :class="{ 'border-brand': search_uploader == 'anyone' }"
            @click.prevent="
              search_uploader = 'anyone';
              own_uploads_only = 0;
              is_searching = true;
              load_more_results = false;
              search();
              getTotalFiles();
            "
            >Uploaded By Anyone</a
          >
        </li>
        <li
          class="mx-3 md:mx-6 flex flex-row flex-none"
          v-is-authorized="{ roles: [role], permissions: ['can_upload'] }"
        >
          <a
            href="#"
            class="h-full border-b-4 border-transparent hover:border-brand no-underline whitespace-no-wrap text-grey-darkest uppercase font-semibold text-xs md:text-base flex items-center"
            :class="{ 'border-brand': search_uploader == 'me' }"
            @click.prevent="
              search_uploader = 'me';
              own_uploads_only = 1;
              is_searching = true;
              load_more_results = false;
              search();
              getTotalFiles();
            "
            >Uploaded By Me</a
          >
        </li>
      </ul>
    </div>

    <div class="px-0 py-6 md:p-6">
      <!-- Same pill search bar as the vault list -->
      <div class="flex items-center mx-6 md:m-0">
        <label for="search-documents" class="uppercase font-semibold hidden md:block mr-4">Search</label>
        <!-- min-w-0 on the form and the input: a text input keeps its intrinsic width inside a flex
             row in Safari and Firefox, which pushed the pill past a 390px screen -->
        <form
          class="search-pill flex-1 min-w-0 border border-grey rounded-full p-2 flex items-center"
          @submit.prevent="search"
        >
          <input
            ref="input"
            id="search-documents"
            type="text"
            class="flex-1 min-w-0 w-full px-3 bg-transparent outline-none"
            v-model="search_text"
            autocomplete="off"
            placeholder="Search Documents"
          />
          <button
            type="button"
            class="w-10 h-10 mr-2 bg-grey text-white flex justify-center items-center rounded-full flex-none"
            v-if="search_text"
            @click.prevent="search_text = ''; search();"
          >
            <i class="fa fa-times fa-lg"></i>
          </button>
          <button
            type="submit"
            class="w-10 h-10 bg-brand text-white flex justify-center items-center rounded-full flex-none hover:bg-brand-darkest"
          >
            <i class="fa fa-search fa-lg"></i>
          </button>
        </form>
      </div>
      <div class="px-6 md:px-0 py-2 text-xs">
        <p class="text-grey-darker">You can search for exact phrase by surrounding the search query using double quotation mark. eg. <span class="font-bold">"invoice"</span></p>
      </div>

      <div class="flex-col py-4 mx-6 md:m-0">
        <!-- Filter bar: a "Date stamp" segmented control for the date range (filing_datestamp, not the upload date), "Show" chips for file state,
             Select Tags on the right. One height everywhere (py-2 text-sm). -->
        <div class="flex flex-col">
          <mq-layout mq="sm_desktop+" class="flex flex-col items-start">
            <div class="w-full flex flex-wrap items-center mb-2">
              <span class="w-20 flex-none text-xxs uppercase tracking-wide text-grey-dark font-semibold">Show</span>
              <button
                key="uncategorised-filter"
                type="button"
                class="border rounded-full my-1 py-2 px-3 mr-2 text-sm font-medium hover:bg-brand hover:text-white hover:border-brand"
                :class="uncategorised ? 'bg-brand text-white border-brand' : 'border-grey text-grey-darker'"
                @click="setUncategorisedFilter"
              >
                Uncategorised
              </button>
              <button
                key="duplicate-filter"
                type="button"
                class="border rounded-full my-1 py-2 px-3 text-sm font-medium hover:bg-brand hover:text-white hover:border-brand"
                :class="duplicate ? 'bg-brand text-white border-brand' : 'border-grey text-grey-darker'"
                @click="setDuplicateFilter"
              >
                Possible Duplicate
              </button>
              <div class="ml-auto pl-4">
                <button
                  type="button"
                  class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded group w-full md:w-auto"
                  @click="handleOpenTags"
                  :class="{ 'bg-brand text-white': open_tags, 'text-brand': !open_tags, 'hover:bg-brand hover:text-white': !uncategorised, 'cursor-not-allowed opacity-50': uncategorised }"
                >
                  <span v-if="tags_data[0].tags.length == 0">
                    <i
                      class="fa fa-tags"
                    ></i>
                    Select Tags
                  </span>
                  <span v-else>
                    <i
                      class="fa fa-tags"
                    ></i>
                    {{ tags_data[0].tags.length }}
                    <span v-if="tags_data[0].tags.length > 1">Tags</span>
                    <span v-else>Tag</span>
                    Selected
                  </span>
                </button>
              </div>
            </div>
            <div class="w-full flex items-center mb-2">
              <span class="w-20 flex-none text-xxs uppercase tracking-wide text-grey-dark font-semibold">Date stamp</span>
              <!-- Segments wrap onto a second line on narrow screens rather than pushing past the edge;
                   my-1 on each keeps a gap between the rows -->
              <div class="flex flex-wrap -my-1">
                <button
                  v-for="(option, index) in options"
                  :key="option.value"
                  type="button"
                  class="border border-grey -ml-px my-1 py-2 px-3 text-sm font-medium text-grey-darker whitespace-no-wrap hover:bg-brand hover:text-white hover:border-brand hover:z-10 relative"
                  :class="{
                    'rounded-l ml-0': index === 0,
                    'rounded-r': index === options.length - 1,
                    'bg-brand text-white border-brand z-10': isDateOptionActive(option.value),
                  }"
                  @click="setDate(option.value)"
                >
                  {{ option.name }}
                </button>
              </div>
            </div>
          </mq-layout>

          <mq-layout :mq="['mobile', 'tablet']" class="w-full md:w-1/2">
            <multiselect
              v-model="selected_search_date_object"
              track-by="value"
              label="name"
              placeholder="Please Select"
              :options="options"
              :showLabels="false"
              name="selected_search_date"
              :allow-empty="false"
              @input="setDate"
            ></multiselect>
            <div class="mt-2">
              <button
                type="button"
                class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded group w-full md:w-auto"
                @click="handleOpenTags"
                :class="{ 'bg-brand text-white': open_tags, 'text-brand': !open_tags, 'hover:bg-brand hover:text-white': !uncategorised, 'cursor-not-allowed opacity-50': uncategorised }"
              >
                <span v-if="tags_data[0].tags.length == 0">
                  <i
                    class="fa fa-tags"
                  ></i>
                  Select Tags
                </span>
                <span v-else>
                  <i
                    class="fa fa-tags"
                  ></i>
                  {{ tags_data[0].tags.length }}
                  <span v-if="tags_data[0].tags.length > 1">Tags</span>
                  <span v-else>Tag</span>
                  Selected
                </span>
              </button>
            </div>
          </mq-layout>

        </div>
        <!-- Custom range: opens under the Date stamp row. The w-20 spacer matches the label width
             so the panel starts where the segments start (there is no ml-20 in this Tailwind scale). -->
        <div class="flex mb-2" v-if="show_custom">
        <span class="w-20 flex-none hidden md:block"></span>
        <form
          @submit.prevent="validateBeforeSearch"
          class="flex-1 p-3 border border-grey-light rounded bg-grey-lightest flex flex-col md:flex-row md:items-end"
        >
          <div class="flex-1 md:mr-3 mb-2 md:mb-0">
            <label class="block text-xs font-semibold mb-1">Date From</label>
            <flat-pickr
              class="appearance-none border rounded w-full py-2 px-3 text-sm text-black bg-white"
              :class="{ 'border-red': errors.has('search_date_from') }"
              placeholder="Select Date From"
              :config="date_from_config"
              v-validate="'required|date_format:DD/MM/YYYY'"
              v-model="custom.date_from"
              name="search_date_from"
              @input="setMinDate(custom.date_from)"
            ></flat-pickr>
          </div>

          <div class="flex-1 md:mr-3 mb-2 md:mb-0">
            <label class="block text-xs font-semibold mb-1">Date To</label>
            <flat-pickr
              class="appearance-none border rounded w-full py-2 px-3 text-sm text-black bg-white"
              :class="{ 'border-red': errors.has('search_date_to') }"
              placeholder="Select Date To"
              :config="date_to_config"
              v-validate="'required|date_format:DD/MM/YYYY'"
              v-model="custom.date_to"
              name="search_date_to"
            ></flat-pickr>
          </div>

          <button
            type="submit"
            class="flex-none border border-brand bg-brand py-2 px-4 text-sm text-white font-medium rounded hover:bg-brand-darkest w-full md:w-auto"
          >
            <i class="fas fa-search mr-2"></i>
            Search
          </button>
        </form>
        </div>
      </div>

      <!-- tablet and above -->
      <mq-layout
        mq="tablet+"
        class="border mt-6 shadow block mx-6 md:m-0"
        v-if="open_tags"
      >
        <div class="w-full text-right flex justify-end">
          <button type="button" class="mt-2 mr-3 flex items-center text-sm" @click="open_tags = !open_tags">
            close
            <i class="fa fa-times fa-lg pl-2"></i>
          </button>
        </div>

        <div class="px-2 pb-2">
          <div class="flex flex-row text-center sm:overflow-x-auto">
            <tags
              v-for="category in categorized_tags"
              :category="category"
              :key="category.id"
              class="category flex-1 mx-4 sm:mx-2"
            >
              <tag
                v-for="tag in category.tags"
                :key="tag.id"
                class="px-1 py-4 h-12 sm:h-16"
                :tag="tag"
                :selected-files="tags_data"
                used-for="searching"
                @tagged="selectTag"
                @untagged="deSelectTag"
                :truncate="true"
              ></tag>
            </tags>
          </div>
        </div>

        <div class="border-t text-center p-3">
          <button
            type="button"
            class="bg-brand py-2 px-6 text-white font-medium rounded hover:bg-brand-darkest"
            @click="open_tags = !open_tags"
          >
            Select
          </button>
        </div>
      </mq-layout>

      <!-- Mobile -->
      <mq-layout mq="mobile" class="mt-6 shadow block" v-if="open_tags">
        <div class="w-full text-right bg-grey-lighter md:bg-white">
          <button class="m-4" @click="open_tags = !open_tags">
            close
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="p-2 bg-grey-lighter">
          <div class="flex flex-row text-center">
            <tabs>
              <tab
                v-for="(category, index) in categorized_tags"
                :key="index"
                :name="category.name"
                :selected="index == 0 ? true : false"
              >
                <tag
                  v-for="tag in category.tags"
                  :key="tag.id"
                  class="p-2"
                  :tag="tag"
                  :selected-files="tags_data"
                  used-for="searching"
                  @tagged="selectTag"
                  @untagged="deSelectTag"
                  :truncate="true"
                ></tag>
              </tab>
            </tabs>
          </div>
        </div>

        <div class="md:border-t text-center p-8">
          <button
            class="bg-brand p-4 px-8 text-white font-medium rounded"
            @click="open_tags = !open_tags"
          >
            Select
          </button>
        </div>
      </mq-layout>

      <div
        class="bg-grey-lightest flex justify-between w-full sm:flex-row flex-col border-b md:px-2 px-6 pt-2 pb-4 mb-6 mt-2"
      >
        <div class="flex-1 w-full md:w-1/2 sm:flex-none my-1 sm:my-0 relative">
          <button
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
            @click.prevent="show_actions = !show_actions"
            :class="{
              'bg-brand text-white': show_actions,
              'text-brand': !show_actions,
            }"
            v-is-authorized="{
              roles: [role],
              permissions: ['can_upload', 'can_download'],
            }"
          >
            <i class="fa fa-cog"></i> Actions
          </button>

          <button
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded ml-2 hover:bg-brand hover:text-white"
            @click="toggleSelectAllFiles"
            :class="{
              'text-brand': !isSelectAllFiles,
              'bg-brand text-white': isSelectAllFiles,
              'cursor-not-allowed opacity-50': search_results.length == 0,
            }"
            :disabled="search_results.length == 0"
            v-is-authorized="{
              roles: [role],
              permissions: ['can_bulk_download'],
            }"
          >
            <i class="fa fa-check-square" v-if="isSelectAllFiles"></i>
            <i class="fa fa-square" v-else></i>
            Select all
          </button>

          <div
            class="bg-white absolute w-64 py-4 px-4 shadow-lg z-20 rounded"
            v-if="show_actions"
          >
            <div
              v-is-authorized="{
                roles: [role],
                permissions: ['can_replace_tag'],
              }"
            >
              <button
                type="button"
                class="group border p-2 rounded border-brand text-brand w-full flex items-center justify-between text-sm"
                @click.prevent="openReplaceTagModal"
                :class="{
                  'hover:bg-brand hover:text-white': selected_files.length > 0,
                  'text-grey cursor-not-allowed opacity-50':
                    selected_files.length == 0,
                }"
                :disabled="selected_files.length == 0"
              >
                <span><i class="fa fa-tag mr-1"></i> Replace Tag</span>
                <span
                  class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                  :class="selected_files.length > 0 ? 'bg-brand text-white group-hover:bg-white group-hover:text-brand' : 'bg-grey-light text-grey-darkest'"
                >{{ selectedFileLength }}</span>
              </button>
            </div>
            <div
              class="mt-2"
              v-is-authorized="{ roles: [role], permissions: ['can_upload'] }"
            >
              <button
                type="button"
                class="group border p-2 rounded border-brand text-brand w-full flex items-center justify-between text-sm"
                @click.prevent="$refs.transfer_file_modal.open()"
                :class="{
                  'hover:bg-brand hover:text-white': selected_files.length > 0,
                  'text-grey cursor-not-allowed opacity-50':
                    selected_files.length == 0,
                }"
                :disabled="selected_files.length == 0"
              >
                <span><i class="fa fa-exchange-alt mr-1"></i> Transfer</span>
                <span
                  class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                  :class="selected_files.length > 0 ? 'bg-brand text-white group-hover:bg-white group-hover:text-brand' : 'bg-grey-light text-grey-darkest'"
                >{{ selectedFileLength }}</span>
              </button>
            </div>

            <div
              v-is-authorized="{
                roles: [role],
                permissions: ['can_bulk_download'],
              }"
            >
              <button
                type="button"
                @click.prevent="$refs.confirm_bulk_exports.open()"
                class="group border p-2 rounded border-brand text-brand mt-2 w-full flex items-center justify-between text-sm"
                :class="{
                  'hover:bg-brand hover:text-white': selected_files.length > 0,
                  'text-grey cursor-not-allowed opacity-50':
                    selected_files.length == 0,
                }"
                :disabled="selected_files.length == 0"
              >
                <span><i class="fa fa-download mr-1"></i> Bulk Export</span>
                <span
                  class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                  :class="selected_files.length > 0 ? 'bg-brand text-white group-hover:bg-white group-hover:text-brand' : 'bg-grey-light text-grey-darkest'"
                >{{ selectedFileLength }}</span>
              </button>
            </div>

            <div
              v-is-authorized="{ roles: [role], permissions: ['can_upload'] }"
            >
              <button
                type="button"
                @click.prevent="openBulkPrintModal"
                class="group border p-2 rounded border-brand text-brand mt-2 w-full flex items-center justify-between text-sm"
                :class="{
                  'hover:bg-brand hover:text-white': selected_files.length > 0,
                  'text-grey cursor-not-allowed opacity-50':
                    selected_files.length == 0,
                }"
                :disabled="selected_files.length == 0"
              >
                <span><i class="fa fa-print mr-1"></i> Bulk Print</span>
                <span
                  class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                  :class="selected_files.length > 0 ? 'bg-brand text-white group-hover:bg-white group-hover:text-brand' : 'bg-grey-light text-grey-darkest'"
                >{{ selectedFileLength }}</span>
              </button>
            </div>

            <div
              v-is-authorized="{
                roles: [role],
                permissions: ['can_delete_document_after_filing'],
              }"
            >
              <button
                type="button"
                @click.prevent="$refs.delete_file_confirm.open()"
                class="group border border-red p-2 rounded text-red mt-2 w-full flex items-center justify-between text-sm"
                :class="{
                  'hover:bg-red hover:text-white': selected_files.length > 0,
                  'text-grey cursor-not-allowed opacity-50':
                    selected_files.length == 0,
                }"
                :disabled="selected_files.length == 0"
              >
                <span><i class="fa fa-trash mr-1"></i> Delete</span>
                <span
                  class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                  :class="selected_files.length > 0 ? 'bg-red text-white group-hover:bg-white group-hover:text-red' : 'bg-grey-light text-grey-darkest'"
                >{{ selectedFileLength }}</span>
              </button>
            </div>

            <p
              class="mt-2 bg-red text-white text-xs py-1 px-2 rounded-full text-center"
              v-show="selected_files.length == 0"
            >
              <i class="fas fa-info-circle"></i> No file selected
            </p>
          </div>
        </div>
        <p
          class="text-sm text-grey-darker self-center mt-2 sm:mt-0 md:text-right"
          v-if="search_results_count"
        >
          {{ resultSummary }}
        </p>
      </div>

      <div class="relative">
        <div v-if="search_results_count">
          <div
            class="block md:hidden w-1/2 bg-brand mx-auto text-white text-center rounded-full p-1 mt-4"
          >
            Swipe for more
          </div>

          <div
            v-if="search_results_count"
            class="flex flex-row overflow-x-scroll"
          >
            <file
              v-for="file in search_results"
              :key="file.id"
              :file="file"
              :selected-files="selected_files"
              :get-file-info="true"
              :checkbox="true"
              v-observe-visibility="
                (isVisible, entry) => visibilityChanged(isVisible, entry, file)
              "
              @select-checkbox="fileSelected"
              @deselect-checkbox="fileDeselected"
              @select="fileSelectedSingle"
              @deselect="fileDeselected"
              :is-first="isFirst(file)"
              :is-last="isLast(file)"
            ></file>

            <div class="my-4 mx-2">
              <div
                v-if="load_more_results"
                class="w-32 h-full flex justify-center items-center flex-col bg-grey-lighter text-grey"
                v-observe-visibility="getNextPage"
              >
                <i class="fa fa-spin fa-circle-notch fa-4x"></i>
                loading...
              </div>
            </div>
          </div>

        </div>

        <div
          v-else
          class="bg-grey-light p-8 h-64 flex justify-center items-center text-xl mt-2"
        >
          No results found
        </div>

        <div
          class="text-center text-xl p-8 absolute inset-0 flex justify-center items-center bg-grey-light"
          v-if="is_searching"
        >
          <i class="fa fa-circle-notch fa-spin"></i>
          <span class="ml-2">Searching...</span>
        </div>
      </div>
    </div>

    <!-- Bulk export -->
    <bulk-export
      v-if="bulkExports.length > 0"
      :bulkExports="bulkExports"
      @exportFileDeleted="getBulkExports()"
    />

    <sweet-modal icon="warning" overlay-theme="dark" ref="confirm_bulk_exports">
      <div class="flex flex-col">
        <span>
          Are you sure you want to bulk exports these
          <strong>{{ selectedFileLength }}</strong> files?
        </span>

        <div class="mt-6">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium"
            @click.prevent="processBulkExports"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium"
            @click="$refs.confirm_bulk_exports.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>

    <sweet-modal icon="warning" overlay-theme="dark" ref="confirm_bulk_prints">
      <div class="flex flex-col">
        <span>
          This feature only support PDF file, are you sure you want to bulk
          print these files?
        </span>

        <ol class="text-left font-medium">
          <li v-for="file in filesToPrint" :key="file.id">
            {{ file.filename }}
          </li>
        </ol>

        <div class="mt-6 flex justify-center">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium flex justify-center mr-1"
            :class="{ 'opacity-50': processingFiles }"
            v-if="processingFiles"
            :disabled="processingFiles"
          >
            <i class="fa fa-spinner fa-spin mr-2"></i>Processing
          </button>
          <button
            v-else
            class="border bg-brand p-4 w-32 text-white rounded font-medium flex justify-center mr-1"
            @click.prevent="processBulkPrint()"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium ml-1"
            @click="$refs.confirm_bulk_prints.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>

    <app-modals
      @navigate-files="navigateFile"
      :with-navigation="true"
    ></app-modals>

    <sweet-modal
      overlay-theme="dark"
      ref="transfer_file_modal"
      title="Transfer File"
    >
      <div class="p-4">
        <div class="p-2 flex flex-wrap">
          <div
            class="w-1/2 relative p-2"
            v-for="(vault, idx) in vaults"
            :key="idx"
          >
            <a
              href="#"
              class="text-grey-darkest no-underline bg-brand-lightest overflow-hidden"
              @click.prevent="
                selected_vault_for_transfer = vault;
                $refs.confirm_file_transfer.open();
              "
            >
              <div
                class="flex flex-col justify-center items-center align-center bg-brand-lightest p-6 hover:shadow"
              >
                <div class="bg-vault-icon"></div>
                <span class="text-lg font-medium">{{ vault.name }}</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </sweet-modal>

    <sweet-modal
      icon="warning"
      overlay-theme="dark"
      ref="confirm_file_transfer"
    >
      <div class="flex flex-col">
        <span v-if="selected_files.length > 1">
          Are you sure you want to transfer these
          <strong>{{ selected_files.length }}</strong> files to Vault
          <strong>{{ selected_vault_for_transfer.name }}</strong
          >?
        </span>

        <span v-else>
          Are you sure you want to transfer this file to Vault
          <strong>{{ selected_vault_for_transfer.name }}</strong
          >?
        </span>

        <div class="mt-6">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium"
            @click.prevent="transferFiles()"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium"
            @click="$refs.confirm_file_transfer.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>

    <sweet-modal
      ref="processing_request"
      overlay-theme="dark"
      hide-close-button
    >
      <div class="mt-4">
        <p class="text-6xl text-grey-dark">
          <i class="fa fa-spin fa-circle-notch"></i>
        </p>
        Please wait...
      </div>
    </sweet-modal>

    <sweet-modal icon="warning" overlay-theme="dark" ref="delete_file_confirm">
      <div class="flex flex-col">
        <span
          >Are you sure you want to delete these
          <strong>{{ selectedFileLength }}</strong> document?</span
        >
        <span class="text-red text-xs font-medium"
          >This document will be deleted permanently</span
        >

        <div class="mt-6">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium"
            @click.prevent="deleteMultipleFile"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium"
            @click="$refs.delete_file_confirm.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>
    <replace-tag
      ref="replaceTag"
      :files="selected_files"
      @replaced="tagsReplaced"
    ></replace-tag>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Carousel, Slide } from "vue-carousel";
import { PDFDocument } from "pdf-lib";
import * as Sentry from "@sentry/vue";
import Tags from "../components/Tagging/Tags";
import Tag from "../components/Tagging/Tag";
import File from "../components/Vault/File";
import AppModals from "../components/AppModals";
import flatPickr from "vue-flatpickr-component";
import Tabs from "../components/FileView/Tabs";
import Tab from "../components/FileView/Tab";
import BulkExport from "../components/FileView/BulkExport";
import ReplaceTag from "../components/Modals/ReplaceTag.vue";

export default {
  name: 'SearchDocuments',
  components: {
    Tags,
    Tag,
    Tabs,
    Tab,
    Carousel,
    Slide,
    File,
    AppModals,
    flatPickr,
    BulkExport,
    ReplaceTag,
  },

  data() {
    return {
      bulkExports: [],
      filesToPrint: [],
      processingFiles: false,
      is_searching: false,

      show_custom: false,

      total_searched_files: 0,
      count: 40,
      page: 1,
      end_of_page: false,
      search_result_range: {
        visible_indexes: [],
        from: 0,
        to: 0,
      },
      load_more_results: false,

      tags_data: [
        {
          tags: [],
        },
      ],
      selected_files: [],
      selected_search_date: "any",
      selected_search_date_object: {
        name: "Today",
        value: "today",
      },
      search_uploader: "anyone",
      search_text: "",
      search_date: {},
      search_recent: 0,
      search_results: [],
      own_uploads_only: 0,

      open_tags: false,

      custom: {
        date_from: new Date(),
        date_to: new Date(),
      },

      date_from_config: {
        wrap: true,
        allowInput: true,
        dateFormat: "d/m/Y",
        defaultDate: new Date(),
        monthSelectorType: "static",
      },

      date_to_config: {
        wrap: true,
        allowInput: true,
        dateFormat: "d/m/Y",
        defaultDate: new Date(),
        monthSelectorType: "static",
      },

      options: [
        { name: "Any", value: "any" },
        { name: "Today", value: "today" },
        { name: "Yesterday", value: "yesterday" },
        { name: "Last Week", value: "last-week" },
        { name: "Last Month", value: "last-month" },
        { name: "3 Months", value: "3-months" },
        { name: "6 Months", value: "6-months" },
        { name: "12 Months", value: "12-months" },
        { name: "Recently Uploaded", value: "recent" },
        { name: "Custom", value: "custom" },
      ],

      show_actions: false,

      selected_vault_for_transfer: "",

      isSelectAllFiles: false,

      fetch_cancel: null,

      totalFiles: null,

      uncategorised: false,

      duplicate: false,
    };
  },

  mounted() {
    this.setDate(this.selected_search_date);

    this.$ebus.$on("updated-from-fileview", (payload) => {
      this.$noty.success(`The document: ${payload.filename} has been updated`, {
        killer: true,
        timeout: 2000,
      });
    });

    this.$ebus.$on("remove-as-selected", (payload) => {
      if(payload && payload.id) {
        this.search_results.splice(
          _.findIndex(this.search_results, ["id", payload.id]),
          1
        );
      }
    });

    this.$ebus.$on("deleted-from-file", (payload) => {
      this.resetSearchResults();
      this.page = 1;
      this.fetchFiles();
    });

    this.$ebus.$on("archived-from-file", (payload) => {
      this.is_searching = true;
      this.page = 1;
      this.resetSearchResults();
    });

    this.$ebus.$on("404-file", (payload) => {
      this.resetSearchResults();
      this.page = 1;
      this.fetchFiles();
    });

    this.getBulkExports();

    this.checkShouldOpenFile();

    this.getTotalFiles();
  },

  methods: {
    isDateOptionActive(value) {
      if (value === "custom") {
        return this.show_custom;
      }
      return this.selected_search_date == value && !this.show_custom;
    },
    handleOpenTags() {
      if(this.uncategorised) {
        return;
      }

      this.open_tags = !this.open_tags;
      this.show_custom = false;
    },
    setDuplicateFilter () {
      this.duplicate = !this.duplicate;
      this.is_searching = true;
      this.search();
      this.resetSearchResults();
    },
    setUncategorisedFilter() {
      this.uncategorised = !this.uncategorised;

      this.tags_data[0].tags = [];
      this.open_tags = false;

      this.is_searching = true;
      this.search();
      this.resetSearchResults();
    },
    getTotalFiles() {
      let uploader_id = "";

      if (this.own_uploads_only == 1) {
        uploader_id = this.user.id;
      }

      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/files/count`, {
          params: {
            own_uploads_only: this.own_uploads_only,
            uploader_id: uploader_id,
          }
        })
        .then((response) => {
          if(response && response.data) {
            this.totalFiles = response.data.count;
          } else {
            this.totalFiles = null;
          }
        }).catch((err) => {
          Sentry.captureException(err);
          this.totalFiles = null;
        });
    },

    tagsReplaced() {
      this.show_actions = false;
      this.selected_files = [];
      // this.search();
    },
    openReplaceTagModal() {
      this.$refs.replaceTag.$refs.modal.open();
    },
    deleteMultipleFile() {
      this.$refs.delete_file_confirm.close();
      this.$refs.processing_request.open();

      let selected_files_length = _.size(this.selected_files);
      let deleted_files_count = 0;
      _.forEach(this.selected_files, (file) => {
        axios
          .delete(`api/vaults/${file.vault_id}/files/${file.id}`)
          .then(({ data }) => {
            if (data) {
              deleted_files_count++;

              if (selected_files_length == deleted_files_count) {
                this.selected_files = [];
                this.$refs.processing_request.close();

                this.$emit("refresh-files", "file-deleted");
                this.$ebus.$emit("deleted-from-file");
                this.$noty.success(`Files has been deleted!`, {
                  killer: true,
                  timeout: 1500,
                });
              }
            }
          })
          .catch((err) => {
            Sentry.captureException(err);

            const { response } = err;
            const data = (response && response.data) || {};

            this.$refs.processing_request.close();
            if (!data.success) {
              if (response && response.status == 404) {
                this.$noty.error("Error on Delete: File not found");
                this.selected_files.splice(
                  _.findIndex(this.selected_files, ["id", file.id]),
                  1
                );
              } else {
                this.$noty.error(data.message);
              }
            }
          });
      });
    },

    checkShouldOpenFile() {
      if (this.$route.query.file) {
        axios
          .get(
            `api/vaults/${this.$route.params.vault_id}/files/${this.$route.query.file}`
          )
          .then((resp) => {
            if (resp.data && resp.data.id) {
              this.$ebus.$emit("view-file", resp.data);
            } else {
              this.$noty.warning("File not found");
            }
          });
      }
    },

    toggleSelectAllFiles() {
      this.isSelectAllFiles = !this.isSelectAllFiles;
      if (this.isSelectAllFiles) {
        // send warning notification
        this.$noty.warning(
          "This action will select all files with applied search and filter"
        );
        this.selected_files = this.search_results;
      } else {
        this.selected_files = [];
      }
    },

    openBulkPrintModal() {
      this.filesToPrint = this.selected_files.filter(
        (file) => file.mime_type == "application/pdf"
      );
      if (this.filesToPrint.length == 0) {
        this.$noty.warning("No PDF files selected");
        return;
      }
      this.$refs.confirm_bulk_prints.open();
    },

    downloadURL(data, fileName) {
      const a = document.createElement("a");
      a.href = data;
      a.download = fileName;
      document.body.appendChild(a);
      a.style.display = "none";
      a.click();
      a.remove();
    },

    downloadBlob(data, fileName, mimeType) {
      const blob = new Blob([data], {
        type: mimeType,
      });
      const url = window.URL.createObjectURL(blob);

      this.downloadURL(url, fileName);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },

    async processBulkPrint() {
      this.processingFiles = true;
      this.$noty.success("Files is being processed.");

      try {
        const mergedPdf = await PDFDocument.create();

        const request = await axios.post(
          `api/vaults/${this.$route.params.vault_id}/bulk-prints/urls`,
          {
            file_ids: this.filesToPrint.map((file) => file.id),
          }
        );

        const urls = await request.data;

        for (let i = 0; i < urls.length; i++) {
          const arrayBuffer = await fetch(urls[i].url).then((res) =>
            res.arrayBuffer()
          );
          const pdf = await PDFDocument.load(arrayBuffer);
          const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
        }

        const mergedPdfFile = await mergedPdf.save();

        this.downloadBlob(
          mergedPdfFile,
          `${moment().format("YYYY-MM-DD h:mm:ss")}.pdf`,
          "application/pdf"
        );

        this.$noty.success("Files successfully merged.");
      } catch (error) {
        this.$noty.danger("Failed to merged files.");
      }

      this.processingFiles = false;

      this.$refs.confirm_bulk_prints.close();
    },

    processBulkExports() {
      this.$refs.confirm_bulk_exports.close();
      this.$refs.processing_request.open();

      let tag_ids = [];
      _.forEach(this.tags_data[0].tags, (value) => {
        tag_ids.push(value.id);
      });

      let uploader_id = "";

      if (this.own_uploads_only == 1) {
        uploader_id = this.user.id;
      }

      let params = {
        query: this.search_text,
        dates: this.search_date,
        own_uploads_only: this.own_uploads_only,
        count: this.count,
        page: this.page,
        uploader_id: uploader_id,
        recent: this.search_recent,
      };

      if (tag_ids.length > 0) {
        params = { ...params, tags: tag_ids };
      }

      axios
        .post(`/api/vaults/${this.$route.params.vault_id}/bulk-exports`, {
          ...params,
          file_ids: _.map(this.selected_files, "id"),
          is_all_files: this.isSelectAllFiles,
        })
        .then(({ data }) => {
          if (data.success) {
            this.$noty.success(data.message);
            this.isSelectAllFiles = false;
            this.selected_files = [];
            this.show_actions = false;
          } else {
            this.$noty.error(data.message);
          }
        })
        .catch(() => {
          this.$noty.error(
            "Something went wrong while requesting the bulk export. Please try again."
          );
        })
        .finally(() => {
          this.$refs.processing_request.close();
          this.getBulkExports();
        });
    },

    getBulkExports() {
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/bulk-exports`)
        .then(({ data }) => {
          this.bulkExports = data;
        });
    },

    setMinDate(date) {
      this.$set(this.date_to_config, "minDate", date);
    },

    setDate(type) {
      let check_is_object = _.isObject(type) ? true : false;
      if (check_is_object) {
        type = type.value;
      }
      this.search_recent = 0;

      if (type != "custom") {
        this.show_custom = false;
        this.selected_search_date = type;
        let date_range = [];
        switch (type) {
          case "today":
            date_range["gte"] = moment().format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "yesterday":
            date_range["gte"] = moment()
              .subtract(1, "days")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "last-week":
            date_range["gte"] = moment()
              .subtract(1, "weeks")
              .startOf("isoWeek")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "last-month":
            date_range["gte"] = moment()
              .subtract(1, "months")
              .startOf("month")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "3-months":
            date_range["gte"] = moment()
              .subtract(3, "months")
              .startOf("month")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "6-months":
            date_range["gte"] = moment()
              .subtract(6, "months")
              .startOf("month")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "12-months":
            date_range["gte"] = moment()
              .subtract(12, "months")
              .startOf("month")
              .format("YYYY/MM/DD");
            date_range["le"] = moment().format("YYYY/MM/DD");
            break;
          case "recent":
            this.search_recent = 1;
            date_range["gte"] = "";
            date_range["le"] = "";
            break;
          default:
            // any
            date_range["gte"] = "";
            date_range["le"] = "";
            break;
        }

        this.search_date.gte = date_range["gte"];
        this.search_date.le = date_range["le"];

        this.search();
        this.is_searching = true;
        this.resetSearchResults();
      } else {
        this.show_custom = true;
        this.open_tags = false;
      }
    },

    selectTag(tag) {
      this.tags_data[0].tags.push({
        id: tag.id,
      });
      this.page = 1;
      this.search();
      this.resetSearchResults();
    },

    deSelectTag(tag) {
      _.forEach(this.tags_data[0].tags, (value, key) => {
        if (value.id == tag.id) {
          this.tags_data[0].tags.splice(key, 1);
          return false;
        }
      });
      this.page = 1;
      this.search();
      this.resetSearchResults();
    },

    removeTags() {
      this.tags_data[0].tags = [];
    },

    validateBeforeSearch() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.search_date.gte = moment(this.custom.date_from, [
            "DD/MM/YYYY",
            "YYYY-MM-DD",
          ]).format("YYYY/MM/DD");
          this.search_date.le = moment(this.custom.date_to, [
            "DD/MM/YYYY",
            "YYYY-MM-DD",
          ]).format("YYYY/MM/DD");
          this.search();
        }
      });
    },

    search() {
      this.page = 1;
      this.resetSearchResults();
      this.fetchFiles();
    },

    resetSearchResults() {
      this.search_result_range.to = 0;
      this.search_result_range.from = 0;
      this.search_result_range.visible_indexes = [];
      this.search_results = [];
    },

    visibilityChanged(isVisible, entry, file) {
      let total_hits = file.total_hits;

      let index = _.findIndex(_.toArray(this.search_results), { id: file.id });

      if (isVisible) {
        // check if index is in array, to avoid duplicates
        if (!_.includes(this.search_result_range.visible_indexes, index)) {
          // add to array
          this.search_result_range.visible_indexes.push(index);
        }
      } else {
        // check if index is in array, to avoid duplicates
        if (_.includes(this.search_result_range.visible_indexes, index)) {
          // remove if in array
          _.remove(this.search_result_range.visible_indexes, (v_index) => {
            return v_index == index;
          });
        }
      }

      this.search_result_range.from =
        _.min(this.search_result_range.visible_indexes) + 1;
      this.search_result_range.to =
        _.max(this.search_result_range.visible_indexes) + 1;
    },

    isEmpty(file) {
      return _.isEmpty(file);
    },

    getNextPage(isVisible, entry) {
      if (isVisible) {
        this.page++;
        this.load_more_files = true;

        this.fetchFiles();
      }
    },

    fileSelected(file) {
      if (_.isNil(_.findKey(this.selected_files, ["id", file.id]))) {
        this.selected_files.push(file);
      }
    },

    fileSelectedSingle(file) {
      if (_.findIndex(this.selected_files, ["id", file.id]) === -1) {
        this.selected_files.push(file);
      }
    },

    fileDeselected(file) {
      this.selected_files.splice(
        _.findIndex(this.selected_files, ["id", file.id]),
        1
      );
    },

    fetchFiles: _.debounce(function (prev_action) {
      let tag_ids = [];
      _.forEach(this.tags_data[0].tags, (value) => {
        tag_ids.push(value.id);
      });

      let uploader_id = "";

      if (this.own_uploads_only == 1) {
        uploader_id = this.user.id;
      }

      let params = {
        query: this.search_text,
        tags: tag_ids,
        dates: this.search_date,
        own_uploads_only: this.own_uploads_only,
        count: this.count,
        page: this.page,
        uploader_id: uploader_id,
        recent: this.search_recent,
        uncategorised: this.uncategorised ? 1 : 0,
        show_duplicate: this.duplicate ? 1 : 0,
      };

      if (this.fetch_cancel) {
        this.fetch_cancel.cancel();
      }

      let cancel_token = axios.CancelToken;
      this.fetch_cancel = cancel_token.source();
      if (prev_action == "file-archived") {
        _.delay(() => {
          axios
            .get(`/api/vaults/${this.$route.params.vault_id}/files`, {
              params: params,
              paramsSerializer: function (params) {
                return qs.stringify(params);
              },
              cancelToken: this.fetch_cancel.token,
            })
            .then(({ data }) => {
              if (!_.isEmpty(this.search_results)) {
                _.each(data, (datum) => {
                  let file_exists = _.find(this.search_results, [
                    "id",
                    datum.id,
                  ]);

                  if (_.isEmpty(file_exists)) {
                    this.search_results.push(datum);
                  }

                  _.uniq(this.search_results);
                });
              } else {
                this.search_results = data;
              }

              if (
                _.size(this.search_results) <
                _.sample(this.search_results).total_hits.value
              ) {
                this.load_more_results = true;
              } else {
                this.load_more_results = false;
              }

              if (_.isEmpty(data)) {
                this.load_more_results = false;
              }

              this.fetch_cancel = null;
              this.is_searching = false;
            })
            .catch((error) => {
              // A superseded request must not touch the state of the one that replaced it
              if (axios.isCancel(error)) {
                return;
              }
              this.fetch_cancel = null;
              this.is_searching = false;
              this.load_more_results = false;
            });
        }, 1500);
      } else {
        axios
          .get(`/api/vaults/${this.$route.params.vault_id}/files`, {
            params: params,
            paramsSerializer: function (params) {
              return qs.stringify(params);
            },
            cancelToken: this.fetch_cancel.token,
          })
          .then(({ data }) => {
            if (!_.isEmpty(this.search_results)) {
              _.each(data, (datum) => {
                let file_exists = _.find(this.search_results, ["id", datum.id]);

                if (_.isEmpty(file_exists)) {
                  this.search_results.push(datum);
                }

                _.uniq(this.search_results);
              });
            } else {
              this.search_results = data;
            }

            if (
              _.size(this.search_results) <
              _.sample(this.search_results).total_hits.value
            ) {
              this.load_more_results = true;
            } else {
              this.load_more_results = false;
            }

            if (_.isEmpty(data)) {
              this.load_more_results = false;
            }

            this.fetch_cancel = null;
            this.is_searching = false;
          })
          .catch((error) => {
            // A superseded request must not touch the state of the one that replaced it
            if (axios.isCancel(error)) {
              return;
            }
            this.fetch_cancel = null;
            this.is_searching = false;
            this.load_more_results = false;
          });
      }
    }, 200),

    navigateFile(payload) {
      let key = _.findKey(_.toArray(this.search_results), [
        "id",
        payload.file.id,
      ]);
      if (!_.isNil(key)) {
        let next_key =
          payload.direction == "next"
            ? _.toInteger(key) + 1
            : _.toInteger(key) - 1;

        if (!_.isNil(_.toArray(this.search_results)[next_key])) {
          let paginate = {
            prev: true,
            next: true,
          };
          let first = _.head(_.toArray(this.search_results));
          let last = _.last(_.toArray(this.search_results));

          if (first.id == _.toArray(this.search_results)[next_key].id) {
            paginate.prev = false;
          }

          if (last.id == _.toArray(this.search_results)[next_key].id) {
            if (this.search_results.length < this.total_hits) {
              this.getNextPage(true);
              paginate.next = true;
            } else {
              paginate.next = false;
            }
          }

          this.$ebus.$emit("open-next", {
            file: _.toArray(this.search_results)[next_key],
            paginate: paginate,
          });
        }
      }
    },

    isFirst(file) {
      let first = _.head(_.toArray(this.search_results));
      return first.id == file.id ? true : false;
    },

    isLast(file) {
      let last = _.last(_.toArray(this.search_results));
      return last.id == file.id && this.total_hits == this.search_results.length
        ? true
        : false;
    },

    transferFiles() {
      this.$refs.confirm_file_transfer.close();
      this.$refs.processing_request.open();

      let count = 0;
      let selected_files_for_transfer_length = this.selected_files.length;

      _.each(this.selected_files, (file) => {
        axios
          .patch(
            `api/vaults/${this.$route.params.vault_id}/files/${file.id}/transfer-vault`,
            {
              vault_id: this.selected_vault_for_transfer.id,
            }
          )
          .then(({ data }) => {
            if (data.success) {
              this.selected_files.splice(
                _.findIndex(this.selected_files, ["id", file.id]),
                1
              );
            }

            count++;

            if (count == selected_files_for_transfer_length) {
              this.$refs.processing_request.close();
              this.$noty.success(
                "File/s has been transfered to " +
                  this.selected_vault_for_transfer.name
              );
              this.show_actions = false;
              _.delay(() => {
                this.resetSearchResults();
                this.page = 1;
                this.fetchFiles();
              }, 1500);
              this.$refs.transfer_file_modal.close();
            }
          })
          .catch(({ response }) => {
            let data = response.data;
            this.$refs.processing_request.close();
            this.$refs.transfer_file_modal.close();
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error("Error: File not found");
              } else {
                this.$noty.error(data.message);
              }
            }
          });
      });
    },
  },

  computed: {
    ...mapGetters({
      categorized_tags: "getCategorizedTags",
      role_id: "getUserRoleId",
      role: "getUserRole",
      user: "getUser",
      vaults: "getVaults",
    }),

    enableNav() {
      if (
        this.total_searched_files == _.sample(this.search_results).total_hits.value
      ) {
        return false;
      } else {
        return true;
      }
    },

    // "Showing 1 to 8 of 42 matching files. 113 files in this vault." The range is what is
    // visible in the strip; it drops when every match is on screen, the second clause drops
    // when no filter narrows the set.
    resultSummary() {
      const fmt = (n) => Number(n).toLocaleString();
      const files = (n) => (Number(n) === 1 ? "file" : "files");
      const total = this.totalFiles != null ? Number(this.totalFiles) : null;
      let matching = Number(this.total_hits) || 0;
      if (total != null && matching > total) {
        matching = total;
      }
      const filtered = total != null && matching < total;
      const from = this.search_result_range.from;
      const to = this.search_result_range.to;
      const allVisible = !(from > 1 || (to > 0 && to < matching));
      const what = `${fmt(matching)}${filtered ? " matching" : ""} ${files(matching)}`;
      const clauses = [allVisible ? what : `Showing ${from} to ${to} of ${what}`];
      if (filtered) {
        clauses.push(`${fmt(total)} files in this vault`);
      }
      return clauses.join(". ");
    },
    total_hits() {
      if (!_.isNil(_.sample(this.search_results))) {
        return _.sample(this.search_results).total_hits.value;
      }
      return 0;
    },

    search_results_count() {
      return _.size(this.search_results);
    },

    selectedFileLength() {
      if (this.isSelectAllFiles) {
        return "All";
      }
      return this.selected_files.length;
    },
  },
};
</script>

<style>
.multiselect__tags {
  min-height: 0px;
  display: block;
  border-radius: 5px;
  border: 1px solid #b8becc;
  background: #fff;
  padding: 10px 40px 4px 8px !important;
}
/* .invalid > .multiselect__tags {
  border: 1px solid #e3342f;
} */
.multiselect__input {
  font-size: 16px;
}
</style>
